import { compareAsc, compareDesc } from "date-fns";
import { get } from "lodash";
import { SortType } from "rsuite-table";

type IsFunction<T> = T extends (...args: unknown[]) => unknown ? true : false;

type Primitive = string | number | boolean | bigint | symbol | null | undefined;
type BuiltInObject =
    | Date
    | RegExp
    | Error
    | Map<unknown, unknown>
    | Set<unknown>;

export type Paths<T, Prefix extends string = ""> = T extends
    | Primitive
    | BuiltInObject
    ? never
    : T extends object
      ? {
            [K in keyof T & string]: IsFunction<T[K]> extends true
                ? never
                : NonNullable<T[K]> extends Primitive | BuiltInObject
                  ? `${Prefix}${K}`
                  : NonNullable<T[K]> extends object
                    ?
                          | `${Prefix}${K}`
                          | Paths<NonNullable<T[K]>, `${Prefix}${K}.`>
                    : `${Prefix}${K}`;
        }[keyof T & string]
      : never;

export const getSorted = <T extends object>(
    data: T[],
    dataKey: keyof T | string | undefined,
    sortType: SortType | undefined
): T[] => {
    if (!dataKey || !sortType) {
        return data;
    }

    const compareValues = (
        x: unknown,
        y: unknown,
        ascending: boolean
    ): number => {
        if (x == null && y == null) return 0;
        if (x == null) return ascending ? 1 : -1;
        if (y == null) return ascending ? -1 : 1;

        if (x instanceof Date && y instanceof Date) {
            return ascending ? compareAsc(x, y) : compareDesc(x, y);
        }

        // Handle string comparison (case-insensitive)
        if (typeof x === "string" && typeof y === "string") {
            const comparison = x.localeCompare(y, undefined, {
                sensitivity: "base",
            });

            return ascending ? comparison : -comparison;
        }

        // Handle equality
        if (x === y) return 0;

        const comparison = x < y ? -1 : 1;

        return ascending ? comparison : -comparison;
    };

    return data.sort((a, b) => {
        const defaultValue = sortType === "asc" ? "\u0000" : "\uFFFF";

        const x = get(a, dataKey, defaultValue);
        const y = get(b, dataKey, defaultValue);

        return compareValues(x, y, sortType === "asc");
    });
};
